import type { ReactNode, ThHTMLAttributes, TdHTMLAttributes } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Botao } from './Botao';
import css from './Dados.module.css';
import { cx } from './cx';

const numero = (n: number) => n.toLocaleString('pt-BR');

/* ── Tabela ─────────────────────────────────────────────────────────────── */

/**
 * Tabela de dados. Rola na horizontal no celular em vez de esmagar colunas.
 * Ponha dentro de `<Cartao padding={0}>`.
 */
export function Tabela({ children, rotulo }: { children: ReactNode; rotulo?: string }) {
  return (
    <div className={css.rolagem}>
      <table className={css.tabela} aria-label={rotulo}>
        {children}
      </table>
    </div>
  );
}

type Alinhar = 'esquerda' | 'direita' | 'centro';

const alinhamento = (a?: Alinhar) =>
  a === 'direita' ? css.direita : a === 'centro' ? css.centro : undefined;

export function Th({
  alinhar,
  encolher,
  className,
  children,
  ...resto
}: ThHTMLAttributes<HTMLTableCellElement> & { alinhar?: Alinhar; encolher?: boolean }) {
  return (
    <th
      scope="col"
      {...resto}
      className={cx(alinhamento(alinhar), encolher && css.encolher, className)}
    >
      {children}
    </th>
  );
}

export function Td({
  alinhar,
  encolher,
  className,
  children,
  ...resto
}: TdHTMLAttributes<HTMLTableCellElement> & { alinhar?: Alinhar; encolher?: boolean }) {
  return (
    <td
      {...resto}
      className={cx(alinhamento(alinhar), encolher && css.encolher, className)}
    >
      {children}
    </td>
  );
}

/** Célula de duas linhas: o principal em destaque e um detalhe embaixo. */
export function Celula({
  principal,
  secundaria,
  para,
}: {
  principal: ReactNode;
  secundaria?: ReactNode;
  /** Torna o principal um link. */
  para?: string;
}) {
  return (
    <>
      {para ? (
        <Link to={para} className={css.celulaPrincipal}>
          {principal}
        </Link>
      ) : (
        <span className={css.celulaPrincipal}>{principal}</span>
      )}
      {secundaria && <span className={css.celulaSecundaria}>{secundaria}</span>}
    </>
  );
}

/** Faixa de filtros no topo de uma tabela (dentro do mesmo cartão). */
export function BarraFerramentas({ children }: { children: ReactNode }) {
  return <div className={css.ferramentas}>{children}</div>;
}

/** Envolve o item da barra que deve ocupar o espaço que sobrar (a busca). */
export function Crescer({ children }: { children: ReactNode }) {
  return <div className={css.ferramentasCrescer}>{children}</div>;
}

/* ── Paginação ──────────────────────────────────────────────────────────── */

export function Paginacao({
  total,
  pagina,
  porPagina,
  aoMudar,
  rotulo = 'registro(s)',
}: {
  total: number;
  pagina: number;
  porPagina: number;
  aoMudar: (p: number) => void;
  rotulo?: string;
}) {
  const paginas = Math.max(1, Math.ceil(total / porPagina));
  const de = total === 0 ? 0 : (pagina - 1) * porPagina + 1;
  const ate = Math.min(total, pagina * porPagina);
  return (
    <nav className={css.paginacao} aria-label="Paginação">
      <span className="num">
        {numero(de)}–{numero(ate)} de {numero(total)} {rotulo}
      </span>
      {paginas > 1 && (
        <div className={css.paginacaoBotoes}>
          <Botao
            aparencia="secundario"
            pequeno
            icone={<ChevronLeft />}
            disabled={pagina <= 1}
            onClick={() => aoMudar(pagina - 1)}
          >
            Anterior
          </Botao>
          <Botao
            aparencia="secundario"
            pequeno
            iconeDireita={<ChevronRight />}
            disabled={pagina >= paginas}
            onClick={() => aoMudar(pagina + 1)}
          >
            Próxima
          </Botao>
        </div>
      )}
    </nav>
  );
}

/* ── Abas segmentadas ───────────────────────────────────────────────────── */

export interface OpcaoAba<T extends string> {
  valor: T;
  rotulo: string;
  contagem?: number;
}

/** Filtro de opções exclusivas ("Todos / Ativos / Pausados"). */
export function Abas<T extends string>({
  opcoes,
  atual,
  aoMudar,
  rotulo,
}: {
  opcoes: ReadonlyArray<OpcaoAba<T>>;
  atual: T;
  aoMudar: (v: T) => void;
  rotulo?: string;
}) {
  return (
    <div className={css.abas} role="group" aria-label={rotulo}>
      {opcoes.map((o) => (
        <button
          key={o.valor}
          type="button"
          className={css.aba}
          aria-pressed={atual === o.valor}
          onClick={() => aoMudar(o.valor)}
        >
          {o.rotulo}
          {o.contagem !== undefined && (
            <span className={css.abaContagem}>{numero(o.contagem)}</span>
          )}
        </button>
      ))}
    </div>
  );
}

/* ── Indicadores (KPI) ──────────────────────────────────────────────────── */

export function Indicadores({ children }: { children: ReactNode }) {
  return <div className={css.indicadores}>{children}</div>;
}

export function Indicador({
  rotulo,
  valor,
  detalhe,
  icone,
  tom,
  para,
}: {
  rotulo: string;
  valor: ReactNode;
  detalhe?: ReactNode;
  icone?: ReactNode;
  /** Pinta o valor (ex.: erro quando há pendências). */
  tom?: 'ok' | 'alerta' | 'erro';
  /** O cartão inteiro leva a esta rota. */
  para?: string;
}) {
  const corpo = (
    <>
      <div className={css.indicadorTopo}>
        <span>{rotulo}</span>
        {icone && (
          <span className={css.indicadorIcone} aria-hidden="true">
            {icone}
          </span>
        )}
      </div>
      <div
        className={cx(
          css.indicadorValor,
          tom === 'ok' && css.tomOk,
          tom === 'alerta' && css.tomAlerta,
          tom === 'erro' && css.tomErro,
        )}
      >
        {valor}
      </div>
      {detalhe && <div className={css.indicadorDetalhe}>{detalhe}</div>}
    </>
  );
  return para ? (
    <Link to={para} className={css.indicador}>
      {corpo}
    </Link>
  ) : (
    <div className={css.indicador}>{corpo}</div>
  );
}

/* ── Definições ─────────────────────────────────────────────────────────── */

/** Pares rótulo/valor (ficha de cliente, detalhes de um job). */
export function Definicoes({ itens }: { itens: ReadonlyArray<readonly [string, ReactNode]> }) {
  return (
    <dl className={css.definicoes}>
      {itens.map(([rotulo, valor]) => (
        <div key={rotulo} style={{ display: 'contents' }}>
          <dt>{rotulo}</dt>
          <dd>{valor}</dd>
        </div>
      ))}
    </dl>
  );
}

/* ── Gráfico de barras ──────────────────────────────────────────────────── */

export interface PontoGrafico {
  /** Rótulo do eixo e da dica ("09/10"). */
  rotulo: string;
  valor: number;
  /** Segunda série, sobreposta (ex.: "com XML completo"). */
  secundario?: number;
}

/**
 * Barras verticais em HTML/CSS puro — sem biblioteca de gráfico, que pesaria
 * mais que o painel inteiro para desenhar 30 colunas.
 */
export function GraficoBarras({
  pontos,
  altura = 180,
  legenda,
  formatar = numero,
}: {
  pontos: PontoGrafico[];
  altura?: number;
  /** Nomes das séries: [principal, secundária]. */
  legenda?: [string, string?];
  formatar?: (v: number) => string;
}) {
  const maximo = Math.max(1, ...pontos.map((p) => Math.max(p.valor, p.secundario ?? 0)));
  const passo = escala(maximo);
  const topo = Math.ceil(maximo / passo) * passo;
  const guias = [topo, topo / 2];

  return (
    <div className={css.grafico}>
      {legenda && (
        <div className={css.graficoLegenda}>
          <span>
            <i aria-hidden="true" /> {legenda[0]}
          </span>
          {legenda[1] && (
            <span>
              <i className={css.secundaria} aria-hidden="true" /> {legenda[1]}
            </span>
          )}
        </div>
      )}
      <div
        className={css.graficoArea}
        style={{ ['--altura' as string]: `${altura}px` }}
        role="img"
        aria-label={`Gráfico de barras com ${pontos.length} pontos`}
      >
        {guias.map((g) => (
          <div
            key={g}
            className={css.graficoGuia}
            style={{ bottom: `${(g / topo) * 100}%` }}
            aria-hidden="true"
          >
            <span>{formatar(g)}</span>
          </div>
        ))}
        {pontos.map((p) => (
          <div key={p.rotulo} className={css.graficoColuna} tabIndex={0}>
            <span className={css.graficoDica}>
              {p.rotulo}: {formatar(p.valor)}
              {p.secundario !== undefined && legenda?.[1]
                ? ` · ${legenda[1]}: ${formatar(p.secundario)}`
                : ''}
            </span>
            <div
              className={css.graficoBarra}
              style={{
                height: `${(p.valor / topo) * 100}%`,
                minHeight: p.valor > 0 ? 2 : 0,
                position: 'relative',
              }}
            >
              {p.secundario !== undefined && p.secundario > 0 && (
                <div
                  className={cx(css.graficoBarra, css.graficoBarraSecundaria)}
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    height: `${(p.secundario / Math.max(p.valor, 1)) * 100}%`,
                  }}
                />
              )}
            </div>
          </div>
        ))}
      </div>
      {pontos.length > 0 && (
        <div className={css.graficoEixo} aria-hidden="true">
          <span>{pontos[0].rotulo}</span>
          {pontos.length > 2 && <span>{pontos[Math.floor(pontos.length / 2)].rotulo}</span>}
          <span>{pontos[pontos.length - 1].rotulo}</span>
        </div>
      )}
    </div>
  );
}

/** Passo "redondo" para as linhas-guia (1, 2, 5, 10, 20, 50…). */
function escala(maximo: number): number {
  const bruto = maximo / 2;
  const potencia = 10 ** Math.floor(Math.log10(bruto));
  const n = bruto / potencia;
  return (n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10) * potencia;
}
